import { IContainer } from '../../interfaces/di/IContainer';
import { ISessionManager } from '../../interfaces/core/ISessionManager';
import { ISessionExecutor } from '../../interfaces/core/ISessionExecutor';
import { ISessionRepository } from '../../interfaces/core/ISessionRepository';
import { ITestEnvironment } from '../../interfaces/test-environment/ITestEnvironment';
import { IEventBus } from '../../interfaces/events/IEventBus';
import { IScriptLoader } from '../../interfaces/utils/IScriptLoader';
import { ICreateSessionUseCase } from '../../interfaces/use-cases/ICreateSessionUseCase';
import { IRunSessionUseCase } from '../../interfaces/use-cases/IRunSessionUseCase';
import { IRunAllSessionsUseCase } from '../../interfaces/use-cases/IRunAllSessionsUseCase';

import { SessionManager } from '../core/SessionManager';
import { SessionExecutor } from '../core/SessionExecutor';
import { InMemorySessionRepository } from '../core/SessionRepository';
import { BrowserTestEnvironment } from '../test-environment/BrowserTestEnvironment';
import { EventBus } from '../events/EventBus';
import { ScriptLoader } from '../utils/ScriptLoader';
import { CreateSessionUseCase } from '../use-cases/CreateSessionUseCase';
import { RunSessionUseCase } from '../use-cases/RunSessionUseCase';
import { RunAllSessionsUseCase } from '../use-cases/RunAllSessionsUseCase';

export class Container implements IContainer {
  private static instance: Container;
  private _sessionManager: ISessionManager;
  private _sessionExecutor: ISessionExecutor;
  private _sessionRepository: ISessionRepository;
  private _testEnvironment: ITestEnvironment;
  private _eventBus: IEventBus;
  private _scriptLoader: IScriptLoader;

  private constructor() {
    this._eventBus = new EventBus();
    this._testEnvironment = new BrowserTestEnvironment();
    this._sessionRepository = new InMemorySessionRepository();
    this._sessionManager = new SessionManager(this._sessionRepository, this._eventBus);
    this._sessionExecutor = new SessionExecutor(this._testEnvironment, this._eventBus);
    this._scriptLoader = new ScriptLoader();
  }

  static getInstance(): Container {
    if (!Container.instance) {
      Container.instance = new Container();
    }
    return Container.instance;
  }

  get sessionManager(): ISessionManager { return this._sessionManager; }
  get sessionExecutor(): ISessionExecutor { return this._sessionExecutor; }
  get sessionRepository(): ISessionRepository { return this._sessionRepository; }
  get testEnvironment(): ITestEnvironment { return this._testEnvironment; }
  get eventBus(): IEventBus { return this._eventBus; }
  get scriptLoader(): IScriptLoader { return this._scriptLoader; }

  setTestEnvironment(testEnvironment: Partial<ITestEnvironment>): void {
    const currentEnv = this._testEnvironment;
    this._testEnvironment = {
      createMochaInstance: testEnvironment.createMochaInstance || currentEnv.createMochaInstance,
      injectGlobals: testEnvironment.injectGlobals || currentEnv.injectGlobals
    };
    
    this._sessionExecutor = new SessionExecutor(this._testEnvironment, this._eventBus);
  }

  createSessionUseCase(): ICreateSessionUseCase {
    return new CreateSessionUseCase(this._sessionManager);
  }

  runSessionUseCase(): IRunSessionUseCase {
    return new RunSessionUseCase(this._sessionManager, this._sessionExecutor);
  }

  runAllSessionsUseCase(): IRunAllSessionsUseCase {
    return new RunAllSessionsUseCase(this._sessionManager, this._sessionExecutor);
  }
}
